import { Anchor } from '@mantine/core';
import type { CSSProperties, ReactNode } from 'react';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { tagsStringToArray } from '@/utils/tags';
import classes from './DataViewer.module.css';

interface DataViewerProps {
  data: Record<string, string>;
  corporateColor?: string;
  /** When set, tag badges become links to marketplace with that tag filter */
  onTagNavigate?: (tag: string) => void;
  children?: ReactNode;
}

function formatFieldLabel(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
}

function isUrlField(key: string, value: string): boolean {
  return key.toLowerCase().includes('page') && value.startsWith('http');
}

export function DataViewer({
  data,
  corporateColor = DEFAULT_QR_CODE_COLOR,
  onTagNavigate,
  children,
}: DataViewerProps) {
  const gridStyle = { '--spec-accent': corporateColor } as CSSProperties;

  const entries = Object.entries(data).filter(
    ([, value]) => value && value.toString().trim() !== ''
  );

  return (
    <div className={classes.grid} style={gridStyle}>
      {entries.map(([key, value]) => {
        const raw = value.toString();
        const isTags = key.toLowerCase() === 'tags';

        return (
          <article
            key={key}
            className={`${classes.specCard} ${isTags ? classes.specCardWide : ''}`}
          >
            <span className={classes.specLabel}>{formatFieldLabel(key)}</span>
            {isTags ? (
              <div className={classes.tagList}>
                {tagsStringToArray(raw).map((tag) =>
                  onTagNavigate ? (
                    <button
                      key={tag}
                      type="button"
                      className={classes.tagButton}
                      onClick={() => onTagNavigate(tag)}
                      title={tag}
                      aria-label={`Filter marketplace by tag ${tag}`}
                    >
                      <span className={classes.tagChip}>
                        <span className={classes.tagHash}>#</span>
                        {tag}
                      </span>
                    </button>
                  ) : (
                    <span key={tag} className={classes.tagChip}>
                      <span className={classes.tagHash}>#</span>
                      {tag}
                    </span>
                  )
                )}
              </div>
            ) : (
              <p className={`${classes.specValue} ${classes.specValueClamped}`}>
                {isUrlField(key, raw) ? (
                  <Anchor
                    href={raw}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.specLink}
                    title={raw}
                  >
                    {raw}
                  </Anchor>
                ) : (
                  raw
                )}
              </p>
            )}
          </article>
        );
      })}
      {children}
    </div>
  );
}
